import { useCallback } from "react";
import {
  deleteManufacturingNamedVariantApplication,
  resetManufacturingNamedVariantApplications,
  updateManufacturingNamedVariantApplication,
} from "@/lib/manufacturing/namedVariantsClient";
import type { NamedVariantMaterialsEffect } from "@/lib/manufacturing/namedVariantMaterialsEffect";

/** Application confirm / price / effect / delete / reset mutations for the variants tab. */
export function useNamedVariantApplicationActions(opts: {
  selectedId: string | null;
  applicationsCount: number;
  setSaveBusy: (busy: boolean) => void;
  setLocalError: (error: string | null) => void;
  reloadDetail: (id: string) => Promise<void>;
  reloadList: () => Promise<void>;
}) {
  const {
    selectedId,
    applicationsCount,
    setSaveBusy,
    setLocalError,
    reloadDetail,
    reloadList,
  } = opts;

  const onConfirmApplications = useCallback(
    async (applicationIds: readonly string[]) => {
      const ids = [...new Set(applicationIds.filter(Boolean))];
      if (ids.length === 0) return;
      setSaveBusy(true);
      setLocalError(null);
      let failed = 0;
      let lastError = "";
      for (const applicationId of ids) {
        const r = await updateManufacturingNamedVariantApplication({
          application_id: applicationId,
          confirm_status: "confirmed",
        });
        if (!r.ok) {
          failed += 1;
          lastError = r.error;
        }
      }
      setSaveBusy(false);
      if (failed > 0) {
        const confirmed = ids.length - failed;
        setLocalError(
          confirmed > 0
            ? `Confirmed ${confirmed}; ${failed} failed${lastError ? ` (${lastError})` : ""}.`
            : lastError || "Confirm failed",
        );
      }
      if (selectedId) await reloadDetail(selectedId);
      await reloadList();
    },
    [selectedId, reloadDetail, reloadList, setSaveBusy, setLocalError],
  );

  const onConfirmApplication = useCallback(
    async (applicationId: string) => {
      await onConfirmApplications([applicationId]);
    },
    [onConfirmApplications],
  );

  const onUpdateApplicationPrices = useCallback(
    async (
      applicationId: string,
      patch: {
        wholesale_price_delta?: number | null;
        retail_price_delta?: number | null;
        included_by_default?: boolean;
      },
    ) => {
      setSaveBusy(true);
      setLocalError(null);
      const r = await updateManufacturingNamedVariantApplication({
        application_id: applicationId,
        ...patch,
      });
      setSaveBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return;
      }
      if (selectedId) await reloadDetail(selectedId);
    },
    [selectedId, reloadDetail, setSaveBusy, setLocalError],
  );

  const onUpdateApplicationEffect = useCallback(
    async (applicationId: string, materials_effect: NamedVariantMaterialsEffect) => {
      setSaveBusy(true);
      setLocalError(null);
      const r = await updateManufacturingNamedVariantApplication({
        application_id: applicationId,
        materials_effect,
      });
      setSaveBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return;
      }
      if (selectedId) await reloadDetail(selectedId);
    },
    [selectedId, reloadDetail, setSaveBusy, setLocalError],
  );

  const onDeleteApplication = useCallback(
    async (applicationId: string) => {
      setSaveBusy(true);
      setLocalError(null);
      const r = await deleteManufacturingNamedVariantApplication({
        application_id: applicationId,
      });
      setSaveBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return;
      }
      if (selectedId) await reloadDetail(selectedId);
      await reloadList();
    },
    [selectedId, reloadDetail, reloadList, setSaveBusy, setLocalError],
  );

  const onResetApplications = useCallback(async () => {
    if (!selectedId || applicationsCount === 0) return;
    setSaveBusy(true);
    setLocalError(null);
    const r = await resetManufacturingNamedVariantApplications({
      named_variant_id: selectedId,
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reloadDetail(selectedId);
    await reloadList();
  }, [
    selectedId,
    applicationsCount,
    reloadDetail,
    reloadList,
    setSaveBusy,
    setLocalError,
  ]);

  return {
    onConfirmApplication,
    onConfirmApplications,
    onUpdateApplicationPrices,
    onUpdateApplicationEffect,
    onDeleteApplication,
    onResetApplications,
  };
}
